import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Radio, X, Activity } from 'lucide-react';

export default function MIDIDebugPanel({ midiDevices, selectedDevice }) {
  const [messages, setMessages] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const maxMessages = 10;

  useEffect(() => {
    if (!selectedDevice) return;

    const handleMessage = (message) => {
      const [status, control, value] = message.data;
      const timestamp = new Date().toLocaleTimeString();
      
      const messageType = 
        status >= 176 && status <= 191 ? 'CC' :
        status >= 144 && status <= 159 ? 'Note' :
        'Other';

      const newMessage = {
        id: Date.now(),
        timestamp,
        status,
        control,
        value,
        type: messageType,
        raw: Array.from(message.data).join(', ')
      };

      setMessages(prev => [newMessage, ...prev].slice(0, maxMessages));
    };

    selectedDevice.onmidimessage = handleMessage;

    return () => {
      if (selectedDevice) {
        selectedDevice.onmidimessage = null;
      }
    };
  }, [selectedDevice]);

  if (!isVisible || midiDevices.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80">
      <Card className="bg-gray-900 border-cyan-500/30 shadow-2xl">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-400 animate-pulse" />
              <span className="text-sm font-bold text-cyan-400">MIDI Monitor</span>
              <Badge className="bg-green-600 text-xs">Live</Badge>
            </div>
            <Button
              size="sm"
              onClick={() => setIsVisible(false)}
              className="bg-gray-700 hover:bg-gray-600 h-6 w-6 p-0"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>

          {selectedDevice && (
            <div className="text-xs text-gray-400 mb-2">
              Device: {selectedDevice.name}
            </div>
          )}

          <div className="space-y-1 max-h-60 overflow-y-auto">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 text-xs py-4">
                <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Waiting for MIDI messages...</p>
                <p className="mt-1">Touch your controller</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className="p-2 bg-gray-800/50 rounded border border-gray-700 text-xs"
                >
                  <div className="flex items-center justify-between mb-1">
                    <Badge className={`${
                      msg.type === 'CC' ? 'bg-cyan-600' :
                      msg.type === 'Note' ? 'bg-purple-600' :
                      'bg-gray-600'
                    } text-xs`}>
                      {msg.type} {msg.control}
                    </Badge>
                    <span className="text-gray-500">{msg.timestamp}</span>
                  </div>
                  <div className="text-gray-300">
                    Value: <span className="text-white font-bold">{msg.value}</span>
                    <span className="text-gray-500 ml-2">({msg.status})</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <Button
            onClick={() => setMessages([])}
            className="w-full mt-2 bg-gray-700 hover:bg-gray-600 text-xs h-7"
          >
            Clear Messages
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
